import {ProjectFieldsService} from '../../fields/project-fields.service';
import {ProjectRendererService} from '../../renderers/project-renderer.service';
import {Injectable} from '@angular/core';
import FeatureLayer from '@arcgis/core/layers/FeatureLayer';
import Graphic from '@arcgis/core/Graphic';
import Polygon from '@arcgis/core/geometry/Polygon';
import {ProjectLayerData} from '../../../../../../pages/geospatial-viewer/model/project';
import {Unsubscribable} from '@core/interfaces/unsubscribable';
import {GeoJSONGeometry, GeoJSONGeometryType} from '../../../../../../pages/geospatial-viewer/model/api';
import Polyline from '@arcgis/core/geometry/Polyline';
import {VisualizationColorCode} from '../../../../../../pages/geospatial-viewer/model/visualization';
import {GenericPopupService} from '@core/utils/engin/geospatial-viewer/popups/generic-popup.service';
import {GeospatialTooltipType} from '../../../../../../pages/geospatial-viewer/model/tooltip.model';
import {BucketDto} from '../../../../../../pages/geospatial-viewer/layers/metric-layer';

@Injectable()
export class ProjectLayerService extends Unsubscribable {
    constructor(
        private fieldsService: ProjectFieldsService,
        private genericPopupService: GenericPopupService,
        private rendererService: ProjectRendererService,
    ) {
        super();
    }

    public prepareProjectLayer(
        data: ProjectLayerData[],
        currentUserId: string,
        layerId: string,
        colorCodeType: VisualizationColorCode,
    ): FeatureLayer {
        return new FeatureLayer({
            id: layerId,
            outFields: ['*'],
            objectIdField: 'ObjectID',
            source: this.createGraphics(data),
            renderer: this.rendererService.customLayerRenderer(colorCodeType),
            fields: this.fieldsService.getCustomMapFields(),
            popupTemplate: {
                title: '{id}',
                outFields: ['*'],
                content: this.genericPopupService.getGenericMetricTooltipFunction.bind(
                    this.genericPopupService,
                    [],
                    null,
                ),
            },
        });
    }

    /*
     * Create array of Graphics, type = polygon. Return Graphic[]
     */
    private createGraphics(data: ProjectLayerData[]): Graphic[] {
        return data.map((project: ProjectLayerData) => {
            const projectGeography: GeoJSONGeometry = JSON.parse(project.shapeGeoJson);
            const isProjectGeographyTypePresent: boolean = [
                GeoJSONGeometryType.LineString,
                GeoJSONGeometryType.MultiLineString,
            ].includes(projectGeography.type);
            let geometry: Polygon | Polyline;

            if (projectGeography.type == GeoJSONGeometryType.Polygon) {
                geometry = new Polygon({
                    rings: projectGeography?.coordinates ? projectGeography.coordinates : [],
                });
            } else if (isProjectGeographyTypePresent) {
                geometry = new Polyline({
                    paths: projectGeography.coordinates,
                });
            }

            return new Graphic({
                geometry: geometry,
                attributes: {
                    // Unique identifiers
                    entityId: project.id + '',
                    entityType: 'PROJECT',
                    tooltipType: GeospatialTooltipType.PROJECT,
                    // Data
                    projectName: project.name,
                    type: project.projectCreateType,
                    status: project.status,
                    owner: project.projectOwnedByCurrentUser ? 'me' : 'other',
                },
            });
        });
    }

    public filterProjectsByCategory(
        projectData: ProjectLayerData[],
        colourCodeType: VisualizationColorCode = VisualizationColorCode.OWNERSHIP,
        projectCategories?: BucketDto[],
    ): ProjectLayerData[] {
        if (!projectCategories || projectCategories.length === 0) {
            return projectData;
        }

        let filteredProjectList: ProjectLayerData[] = [];

        if (colourCodeType === VisualizationColorCode.OWNERSHIP) {
            filteredProjectList = projectData.filter((project: ProjectLayerData) =>
                projectCategories.some(
                    (category: BucketDto) =>
                        (category.key === 'ME' && project.projectOwnedByCurrentUser) ||
                        (category.key === 'ANYONE_ELSE' && !project.projectOwnedByCurrentUser),
                ),
            );
        } else if (colourCodeType === VisualizationColorCode.STATUS) {
            filteredProjectList = projectData.filter((project: ProjectLayerData) =>
                projectCategories.some((category: BucketDto) => category.key === project.status),
            );
        }

        return filteredProjectList;
    }
}
